import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Package,
  Star,
  Trash2,
  ZoomIn,
} from 'lucide-react';
import PosV2StockBadge from './PosV2StockBadge';
import { fmt, resolvePosCatalogPrice } from './posV2Utils';
import {
  resolveProductPhotoPaths,
  resolveProductPhotos,
} from './posV2PhotoUtils';

const PosV2ProductDrawer = ({ api, product, sessionMag, onAdd, onClose, onPhotoUpdated }) => {
  const [detail, setDetail] = useState(product);
  const [loading, setLoading] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [photoBust, setPhotoBust] = useState(() => Date.now());
  const [zoomOpen, setZoomOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const applyDetail = useCallback((data) => {
    if (!data) return;
    const hasPhotos = (Array.isArray(data.photos) && data.photos.length > 0) || data.thumbnail_url;
    if (!data.found && !hasPhotos) return;
    setDetail((prev) => ({ ...prev, ...data }));
    setPhotoBust(Date.now());
    onPhotoUpdated?.(data);
  }, [onPhotoUpdated]);

  const loadDetail = useCallback(() => {
    if (!product?.reference || !api) return Promise.resolve();
    setLoading(true);
    return api.get(`/stock/catalog/${encodeURIComponent(product.reference)}`, {
      params: { session_magasin: sessionMag },
    })
      .then((res) => {
        if (res.data?.found) applyDetail(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, applyDetail, product?.reference, sessionMag]);

  useEffect(() => {
    setPhotoIdx(0);
    setUploadError('');
    setPreviewUrl(null);
    setDetail(product);
    loadDetail();
  }, [loadDetail, product?.reference, sessionMag]);

  const photoPaths = useMemo(() => resolveProductPhotoPaths(detail), [detail]);
  const photos = useMemo(() => {
    if (previewUrl) return [previewUrl];
    return resolveProductPhotos(detail, photoBust);
  }, [detail, photoBust, previewUrl]);
  const activePhoto = photos[photoIdx] || null;
  const activePath = photoPaths[photoIdx] || photoPaths[0] || '';

  if (!product) return null;

  const pricing = resolvePosCatalogPrice(detail || product);
  const prevPhoto = () => setPhotoIdx((i) => (i <= 0 ? photos.length - 1 : i - 1));
  const nextPhoto = () => setPhotoIdx((i) => (i >= photos.length - 1 ? 0 : i + 1));

  const handleUpload = async (file) => {
    if (!file || !product?.reference) return;
    setUploading(true);
    setUploadError('');
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setPhotoIdx(0);
    try {
      const form = new FormData();
      form.append('file', file, file.name || 'photo.jpg');
      const res = await api.post(
        `/stock/catalog/${encodeURIComponent(product.reference)}/photo`,
        form,
        {
          params: { session_magasin: sessionMag },
          timeout: 60000,
        },
      );
      if (res.data?.__offline) {
        throw new Error('Connexion instable — photo non envoyée');
      }
      if (!res.data?.found && !(res.data?.photos?.length || res.data?.thumbnail_url)) {
        throw new Error('Réponse serveur incomplète après upload');
      }
      applyDetail(res.data);
      setPreviewUrl(null);
      setPhotoIdx(0);
    } catch (err) {
      setUploadError(
        err.response?.data?.detail || err.message || 'Upload impossible',
      );
      setPreviewUrl(null);
      loadDetail();
    } finally {
      URL.revokeObjectURL(localPreview);
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const setPrimary = async (path) => {
    if (!path || photoBusy) return;
    setPhotoBusy(true);
    setUploadError('');
    try {
      const res = await api.put(
        `/stock/catalog/${encodeURIComponent(product.reference)}/photo/primary`,
        null,
        { params: { session_magasin: sessionMag, photo: path } },
      );
      applyDetail(res.data);
      setPhotoIdx(0);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Erreur photo principale');
    } finally {
      setPhotoBusy(false);
    }
  };

  const deletePhoto = async (path) => {
    if (!path || photoBusy) return;
    if (!window.confirm('Supprimer cette photo ?')) return;
    setPhotoBusy(true);
    setUploadError('');
    try {
      const res = await api.delete(
        `/stock/catalog/${encodeURIComponent(product.reference)}/photo`,
        { params: { session_magasin: sessionMag, photo: path } },
      );
      applyDetail(res.data);
      setPhotoIdx(0);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Suppression impossible');
    } finally {
      setPhotoBusy(false);
    }
  };

  return (
    <>
      <div className="relative aspect-square rounded-xl overflow-hidden bg-black/40 mb-3 border border-white/10 group">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-10">
            <Loader2 className="animate-spin text-gray-400" size={28} />
          </div>
        )}
        {activePhoto ? (
          <>
            <img
              key={activePhoto}
              src={activePhoto}
              alt=""
              className="w-full h-full object-contain p-2"
              onError={() => loadDetail()}
            />
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg bg-black/60 text-[10px] text-gray-200 opacity-0 group-hover:opacity-100 transition"
            >
              <ZoomIn size={12} /> Zoom
            </button>
            {photos.length > 1 && (
              <>
                <button type="button" onClick={prevPhoto} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80">
                  <ChevronLeft size={18} />
                </button>
                <button type="button" onClick={nextPhoto} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/60 hover:bg-black/80">
                  <ChevronRight size={18} />
                </button>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
            <Package size={48} strokeWidth={1.25} className="opacity-35 mb-2" />
            <span className="text-xs text-gray-600">Photo à venir</span>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/*,.jpg,.jpeg,.png"
          className="hidden"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || photoBusy}
          className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-black/70 hover:bg-black/90 text-xs font-semibold border border-white/20 text-gray-200"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          {uploading ? 'Envoi…' : 'Ajouter'}
        </button>
      </div>

      {uploadError && (
        <p className="text-xs text-red-300 mb-2">{uploadError}</p>
      )}

      {photos.length > 0 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {photos.map((url, idx) => {
            const path = photoPaths[idx];
            const isPrimary = idx === 0;
            return (
              <div key={`${path || url}-${idx}`} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setPhotoIdx(idx)}
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 block ${
                    idx === photoIdx ? 'border-red-500' : 'border-white/10'
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
                <div className="absolute -bottom-1 left-0 right-0 flex justify-center gap-0.5">
                  {!isPrimary && path && (
                    <button
                      type="button"
                      title="Photo principale"
                      disabled={photoBusy}
                      onClick={() => setPrimary(path)}
                      className="p-0.5 rounded bg-black/80 text-amber-300"
                    >
                      <Star size={10} />
                    </button>
                  )}
                  {path && photos.length > 0 && (
                    <button
                      type="button"
                      title="Supprimer"
                      disabled={photoBusy}
                      onClick={() => deletePhoto(path)}
                      className="p-0.5 rounded bg-black/80 text-red-300"
                    >
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h3 className="text-xl font-bold mb-1 text-white">{detail?.designation || product.designation}</h3>
      <p className="text-sm text-gray-400 mb-1">{detail?.reference || product.reference}</p>
      {detail?.ean && <p className="text-xs text-gray-500 mb-2">EAN {detail.ean}</p>}
      {detail?.categorie && (
        <p className="text-xs text-gray-500 mb-2">Catégorie · {detail.categorie}</p>
      )}
      <div className="mb-4">
        {pricing.hasPromo && (
          <p className="text-lg text-gray-500 line-through tabular-nums">{fmt(pricing.base)} €</p>
        )}
        <p className={`text-3xl font-bold tabular-nums ${pricing.hasPromo ? 'text-emerald-400' : 'text-red-400'}`}>
          {fmt(pricing.eff)} €
        </p>
        {pricing.hasPromo && (
          <p className="text-xs text-emerald-300/90 mt-1 font-semibold uppercase tracking-wide">Prix promo appliqué au POS</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <PosV2StockBadge
          sessionMag={sessionMag}
          stockMag={detail?.stock_magasin_id || product.stock_magasin_id}
          stockLocal={detail?.stock_local ?? product.stock_local}
          size="md"
        />
        {photos.length > 0 && (
          <span className="px-2 py-1 rounded-full text-[10px] bg-white/10 text-gray-300">
            {photos.length} photo{photos.length > 1 ? 's' : ''}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-6">
        <div className="p-3 rounded-lg bg-black/30 border border-white/5">
          <div className="text-gray-500 text-xs">Stock M1</div>
          <div className="font-bold text-white">{detail?.availability?.M1 ?? product.availability?.M1 ?? 0}</div>
        </div>
        <div className="p-3 rounded-lg bg-black/30 border border-white/5">
          <div className="text-gray-500 text-xs">Stock M2</div>
          <div className="font-bold text-white">{detail?.availability?.M2 ?? product.availability?.M2 ?? 0}</div>
        </div>
      </div>
      <button
        type="button"
        onClick={() => { onAdd(detail || product); onClose(); }}
        className="w-full min-h-[56px] rounded-xl bg-red-600 hover:bg-red-500 font-bold text-lg text-white"
      >
        Ajouter au panier
      </button>

      {zoomOpen && activePhoto && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setZoomOpen(false)}>
          <img src={activePhoto} alt="" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
          <button type="button" onClick={() => setZoomOpen(false)} className="absolute top-4 right-4 px-3 py-2 rounded-lg bg-white/10">
            Fermer
          </button>
        </div>
      )}
    </>
  );
};

export default PosV2ProductDrawer;
